// Healthy Century — interacción de las secciones propias (hc-*).
// Añadir al carrito se integra con el carrito lateral de Horizon mediante CartLinesUpdateEvent.
import { CartLinesUpdateEvent } from '@shopify/events';

const money = (cents) =>
  new Intl.NumberFormat(document.documentElement.lang || 'es-ES', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'EUR',
  }).format(cents / 100);

/**
 * Añade varias líneas al carrito y avisa al tema para que refresque y abra el carrito lateral.
 * @param {{id: number|string, quantity: number, selling_plan?: string}[]} items
 * @param {Element} source
 */
export async function hcAddToCart(items, source = document.body) {
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
      promise,
    })
  );

  try {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    const data = await res.json();
    const cartRes = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
    const cart = await cartRes.json();
    resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: !res.ok,
        items: cart.items,
        source: 'hc',
        itemCount: items.reduce((n, i) => n + i.quantity, 0),
      },
    });
    if (!res.ok) throw new Error(data.description || data.message || 'No se pudo añadir');
    updateShipBars(cart.total_price);
    return cart;
  } catch (error) {
    reject(error);
    throw error;
  }
}

/* ---------- Botones "añadir" de tarjetas, packs y upsells ---------- */
document.addEventListener('click', async (event) => {
  const btn = event.target instanceof Element ? event.target.closest('[data-hc-add]') : null;
  if (!btn) return;
  event.preventDefault();
  if (btn.getAttribute('aria-busy') === 'true') return;

  const raw = btn.getAttribute('data-hc-add') || '';
  const items = raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((id) => ({ id: Number(id), quantity: 1 }));
  if (!items.length) return;

  btn.setAttribute('aria-busy', 'true');
  try {
    const code = btn.getAttribute('data-hc-code');
    // El código se guarda en la sesión antes de refrescar la cesta, así el descuento ya sale aplicado.
    if (code) await fetch(`${window.Shopify?.routes?.root || '/'}discount/${encodeURIComponent(code)}`);
    await hcAddToCart(items, btn);
    btn.classList.add('is-added');
    setTimeout(() => btn.classList.remove('is-added'), 1800);
  } catch (error) {
    console.warn('[hc] add to cart', error);
    const msg = error instanceof Error ? error.message : '';
    if (msg) window.alert(msg);
  } finally {
    btn.removeAttribute('aria-busy');
  }
});

/* ---------- Barra de envío gratis ---------- */
function updateShipBars(totalCents) {
  document.querySelectorAll('[data-hc-shipbar]').forEach((bar) => {
    const goal = Number(bar.getAttribute('data-goal')) || 0;
    if (!goal) return;
    const total = typeof totalCents === 'number' ? totalCents : Number(bar.getAttribute('data-total')) || 0;
    const left = Math.max(goal - total, 0);
    const pct = Math.min((total / goal) * 100, 100);
    const text = bar.querySelector('[data-hc-shipbar-text]');
    const fill = bar.querySelector('[data-hc-shipbar-fill]');
    if (fill instanceof HTMLElement) fill.style.width = `${pct}%`;
    bar.classList.toggle('hc-shipbar--done', left === 0);
    if (text) {
      text.innerHTML =
        left === 0
          ? bar.getAttribute('data-done') || '¡Tienes el envío gratis!'
          : (bar.getAttribute('data-todo') || 'Te faltan [x] para el envío gratis').replace(
              '[x]',
              `<strong>${money(left)}</strong>`
            );
    }
  });
}
document.addEventListener('shopify:cart:lines-update', (event) => {
  // @ts-ignore
  event.promise?.then(({ cart }) => {
    const total = cart?.cost?.subtotalAmount?.amount;
    if (total != null) updateShipBars(Math.round(Number(total) * 100));
  });
});

/* ---------- Slider del hero ---------- */
class HcSlider extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-viewport]');
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.dots = [...this.querySelectorAll('[data-dot]')];
    if (!this.viewport || this.slides.length < 2) return;

    this.index = 0;
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.index))));

    this.viewport.addEventListener(
      'scroll',
      () => {
        const i = Math.round(this.viewport.scrollLeft / this.viewport.clientWidth);
        if (i !== this.index) this.setActive(i);
      },
      { passive: true }
    );

    const delay = Number(this.dataset.autoplay) * 1000;
    if (delay && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const tick = () => this.go(this.index + 1);
      let timer = setInterval(tick, delay);
      const stop = () => clearInterval(timer);
      const start = () => {
        stop();
        timer = setInterval(tick, delay);
      };
      this.addEventListener('pointerenter', stop);
      this.addEventListener('pointerleave', start);
      this.addEventListener('focusin', stop);
    }
  }

  go(i) {
    const n = this.slides.length;
    const next = (i + n) % n;
    this.viewport.scrollTo({ left: next * this.viewport.clientWidth, behavior: 'smooth' });
    this.setActive(next);
  }

  setActive(i) {
    this.index = i;
    this.dots.forEach((d) => d.setAttribute('aria-current', String(Number(d.dataset.index) === i)));
  }
}
if (!customElements.get('hc-slider')) customElements.define('hc-slider', HcSlider);

/* ---------- Pestañas + carril de productos ---------- */
class HcTabs extends HTMLElement {
  connectedCallback() {
    const tabs = [...this.querySelectorAll('[role="tab"]')];
    const panels = [...this.querySelectorAll('[role="tabpanel"]')];
    tabs.forEach((tab, i) =>
      tab.addEventListener('click', () => {
        tabs.forEach((t, j) => t.setAttribute('aria-selected', String(i === j)));
        panels.forEach((p, j) => (p.hidden = i !== j));
      })
    );
    this.addEventListener('click', (event) => {
      const btn = event.target instanceof Element ? event.target.closest('[data-rail]') : null;
      if (!btn) return;
      const track = this.querySelector('[role="tabpanel"]:not([hidden]) .hc-rail__track');
      if (!track) return;
      const dir = btn.getAttribute('data-rail') === 'prev' ? -1 : 1;
      track.scrollBy({ left: dir * track.clientWidth * 0.8, behavior: 'smooth' });
    });
  }
}
if (!customElements.get('hc-tabs')) customElements.define('hc-tabs', HcTabs);

/* ---------- Animación de entrada ---------- */
if ('IntersectionObserver' in window) {
  const io = new IntersectionObserver(
    (entries) =>
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        }
      }),
    { rootMargin: '0px 0px -8% 0px' }
  );
  const scan = () => document.querySelectorAll('.hc-reveal:not(.is-in)').forEach((el) => io.observe(el));
  scan();
  document.addEventListener('shopify:section:load', scan);
} else {
  document.querySelectorAll('.hc-reveal').forEach((el) => el.classList.add('is-in'));
}

/* ---------- Cabecera: menú móvil, cesta y contador ---------- */
const hcDrawer = document.querySelector('[data-hc-drawer]');
const setDrawer = (open) => {
  if (!hcDrawer) return;
  hcDrawer.classList.toggle('is-open', open);
  hcDrawer.setAttribute('aria-hidden', String(!open));
  document.documentElement.style.overflow = open ? 'hidden' : '';
  if (open) hcDrawer.querySelector('[data-hc-drawer-close]:not([tabindex])')?.focus();
};
document.addEventListener('click', (event) => {
  const t = event.target instanceof Element ? event.target : null;
  if (!t) return;
  if (t.closest('[data-hc-drawer-open]')) setDrawer(true);
  else if (t.closest('[data-hc-drawer-close]')) setDrawer(false);

  const cartLink = t.closest('[data-hc-cart-open]');
  if (cartLink) {
    const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
    if (drawer && (typeof drawer.open === 'function' || typeof drawer.toggle === 'function')) {
      event.preventDefault();
      typeof drawer.open === 'function' ? drawer.open() : drawer.toggle();
    }
  }

  const coupon = t.closest('[data-hc-copy]');
  if (coupon) {
    const code = coupon.getAttribute('data-hc-copy') || '';
    navigator.clipboard?.writeText(code).then(() => {
      coupon.classList.add('is-copied');
      setTimeout(() => coupon.classList.remove('is-copied'), 1600);
    });
  }
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && hcDrawer?.classList.contains('is-open')) setDrawer(false);
});
document.addEventListener('shopify:cart:lines-update', (event) => {
  // @ts-ignore
  event.promise?.then(({ cart }) => {
    const qty = cart?.totalQuantity ?? cart?.lines?.reduce?.((n, l) => n + (l.quantity || 0), 0);
    if (qty == null) return;
    document.querySelectorAll('[data-hc-cart-count]').forEach((el) => {
      el.textContent = String(qty);
      el.setAttribute('data-count', String(qty));
    });
  });
});

/* ---------- Productos relacionados ---------- */
class HcRelated extends HTMLElement {
  connectedCallback() {
    if (this.querySelector('.hc-card')) return;
    const load = () =>
      fetch(this.dataset.url || '')
        .then((r) => r.text())
        .then((html) => {
          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('hc-related');
          if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
        })
        .catch(() => {});
    'requestIdleCallback' in window ? requestIdleCallback(load, { timeout: 2000 }) : setTimeout(load, 600);
  }
}
if (!customElements.get('hc-related')) customElements.define('hc-related', HcRelated);

/* ---------- Popup de bienvenida ---------- */
(() => {
  const dlg = /** @type {HTMLDialogElement | null} */ (document.getElementById('hc-popup'));
  if (!dlg || typeof dlg.showModal !== 'function') return;
  const KEY = 'hc-popup-closed';
  const days = Number(dlg.dataset.days) || 7;
  const store = {
    get() { try { return Number(localStorage.getItem(KEY)) || 0; } catch { return 0; } },
    set() { try { localStorage.setItem(KEY, String(Date.now())); } catch {} },
  };
  const open = () => {
    if (dlg.open) return;
    dlg.showModal();
    cleanup();
  };
  const close = () => {
    store.set();
    dlg.close();
  };
  dlg.addEventListener('click', (e) => {
    if (e.target === dlg) close();
    if (e.target instanceof Element && e.target.closest('[data-hc-popup-close]')) close();
  });
  dlg.addEventListener('cancel', (e) => { e.preventDefault(); close(); });
  dlg.querySelector('form')?.addEventListener('submit', () => store.set());

  // Tras suscribirse (o error) se vuelve a abrir para enseñar el resultado
  if (dlg.querySelector('[data-hc-popup-success]') || dlg.hasAttribute('data-force')) { open(); return; }
  if (Date.now() - store.get() < days * 864e5) return;

  let timer = setTimeout(open, (Number(dlg.dataset.delay) || 8) * 1000);
  const scroller = document.querySelector('.page-wrapper') || window;
  const onScroll = () => {
    const el = scroller === window ? document.documentElement : /** @type {HTMLElement} */ (scroller);
    const y = scroller === window ? window.scrollY : el.scrollTop;
    if (y > (el.scrollHeight - el.clientHeight) * 0.5) open();
  };
  const onLeave = (e) => { if (e.clientY <= 0) open(); };
  function cleanup() {
    clearTimeout(timer);
    scroller.removeEventListener('scroll', onScroll);
    document.removeEventListener('mouseout', onLeave);
  }
  scroller.addEventListener('scroll', onScroll, { passive: true });
  if (matchMedia('(pointer: fine)').matches) document.addEventListener('mouseout', onLeave);
})();
